import { Component, Element, h, Host, Method, Prop, State, Watch } from '@stencil/core'
import { debounce } from 'lodash-es'
import { NanoPopPosition } from 'nanopop'
import { gsap } from 'src/lib/gsap'
import { SpacingType } from 'src/tailwind'

import { classes, colorClasses } from '../../utils/css'

// Lightweight version of ui-tooltip that does not intercept mouse events
// (and thus does not, e.g., interfere with buttons).
// Preferred for simple information display.
// TODO(aleksclark): `on` prop needed to support activation on click or hover
@Component({
  tag: 'ui-infotip',
})
export class Infotip {
  @Element() el: HTMLUiInfotipElement

  @Prop() anchor: NanoPopPosition = 'top'
  @Prop() bgColor = '#5D5D5D'
  @Prop() borderColor = this.bgColor
  @Prop() margin = 15
  @Prop() contentWidth: SpacingType

  hideTimeout: number

  @State() active = false

  onParentEnter = () => {
    this.onParentLeave.cancel()
    this.over = true
    this.active = true
  }

  onParentLeave = debounce(() => {
    this.over = false
    if (!this.ready) {
      return
    }
    this.active = false
  }, 100)

  ready: boolean
  over: boolean

  popperEl: HTMLUiPopperElement
  contentEl: HTMLDivElement

  @Method()
  async close() {
    this.active = false
  }

  @Watch('active')
  onActive() {
    this.popperEl.visible = this.active

    if (this.active) {
      this.ready = false
      window.clearTimeout(this.hideTimeout)
      gsap.killTweensOf(this.contentEl)
      const transformOrigin = {
        'top': 'center bottom',
        'top-middle': 'center bottom',
        'top-start': 'left bottom',
        'top-end': 'right bottom',
        'right': 'left center',
        'right-middle': 'left center',
        'right-start': 'left top',
        'right-end': 'left bottom',
        'bottom': 'center top',
        'bottom-middle': 'center top',
        'bottom-start': 'left top',
        'bottom-end': 'right top',
        'left': 'right center',
        'left-middle': 'right center',
        'left-start': 'right top',
        'left-end': 'right bottom',
      }[this.popperEl.currentPosition]

      gsap.fromTo(
        this.contentEl,
        { scale: 0.8, transformOrigin },
        {
          scale: 1,
          duration: 0.2,
          ease: 'back.out',
          onComplete: () => {
            this.ready = true
            if (!this.over) {
              this.onParentLeave()
            }
          },
        }
      )
    }
  }

  componentDidRender() {
    this.el.parentElement?.addEventListener('mouseenter', this.onParentEnter)
    this.el.parentElement?.addEventListener('mouseleave', this.onParentLeave)
  }

  disconnectedCallback() {
    this.el.parentElement?.removeEventListener('mouseover', this.onParentEnter)
    this.el.parentElement?.removeEventListener('mouseleave', this.onParentLeave)
  }

  render() {
    return (
      <Host class='absolute pointer-events-none'>
        <ui-popper
          margin={this.margin}
          ref={(el) => (this.popperEl = el)}
          position={this.anchor}
          reference={this.el.parentElement}
        >
          <div
            ref={(el) => (this.contentEl = el)}
            class={`
              font-semibold normal-case px-7px py-4px z-pin border text-xs rounded text-white opacity-100
              ${classes(!!this.contentWidth, `w-${this.contentWidth}`)}
              ${colorClasses({
                bg: this.bgColor,
                border: this.borderColor,
              })}
            `}
            style={
              this.bgColor !== 'transparent' && {
                boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 2px 15px -4px rgba(0,0,0,0.1)',
              }
            }
          >
            <slot />
          </div>
          <ui-pointer fillColor={this.bgColor} strokeColor={this.borderColor} strokeWidth={1} width={16} height={8} />
        </ui-popper>
      </Host>
    )
  }
}
